import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEO } from '../components/SEO';
import { TrustBadges } from '../components/TrustBadges';
import { CONTACT } from '../lyphline';
import { PHOTO } from '../workshopPhotos';
import { WorkshopGallery } from '../components/WorkshopGallery';
import {
  Wrench, Truck, Snowflake, ShieldCheck, MapPin, TrendingDown,
  ArrowRight, Phone, CheckCircle2, Cpu, Thermometer, Zap, Gauge, Settings, Activity
} from 'lucide-react';

const IMG = {
  hero: PHOTO.controlHousing,
  gauges: PHOTO.serviceValve,
  panel: PHOTO.boardTest,
};

const SYSTEMS = [
  { icon: Cpu, label: 'Controls' },
  { icon: Thermometer, label: 'Heating systems' },
  { icon: Activity, label: 'Sensors' },
  { icon: Zap, label: 'Electrical protection' },
  { icon: Gauge, label: 'Vacuum components' },
  { icon: Snowflake, label: 'Refrigeration' },
  { icon: Settings, label: 'Wiring & logic' },
  { icon: ShieldCheck, label: 'Safety systems' },
];

/* ---------- Section wrapper: text + image ---------- */
const SplitSection = ({ eyebrow, title, children, img, imgAlt, reverse, bg = 'bg-white', testid }) => (
  <section className={`py-16 sm:py-20 section-padding ${bg}`} data-testid={testid}>
    <div className={`max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center ${reverse ? '' : ''}`}>
      <div className={reverse ? 'lg:order-last' : ''}>
        {eyebrow && <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">{eyebrow}</span>}
        <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">{title}</h2>
        <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">{children}</div>
      </div>
      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl aspect-[4/3]">
        <img src={img} alt={imgAlt} className="w-full h-full object-cover" />
      </div>
    </div>
  </section>
);

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Our Story, We Rebuild Freeze Dryers Like a Good Shop Rebuilds a Vehicle"
        description="LyphLine started because freeze dryer owners kept coming to us with aging, broken or out-of-warranty machines that still had a good foundation. We look at the whole machine, correct what matters, and put it back to work."
        keywords="freeze dryer rebuild, freeze dryer rebuild story, LyphLine, refrigeration rebuild, freeze dryer modification, Canadian freeze dryer service"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#003456]" data-testid="about-hero">
        <img src={IMG.hero} alt="A LyphLine technician rebuilding a freeze dryer control housing at the bench" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#003456] via-[#003456]/92 to-[#003456]/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-20">
          <span className="inline-flex items-center gap-2 bg-[#CE8946]/15 text-[#CE8946] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] border border-[#CE8946]/30">
            <Truck className="h-4 w-4" /> Our Story
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-6 max-w-4xl leading-[1.08]">
            We treat freeze dryers the way a good shop treats a vehicle
          </h1>
          <p className="text-lg text-white/75 mt-6 max-w-2xl leading-relaxed">
            LyphLine started because more and more freeze dryer owners kept coming to us with the same
            problem, aging, out of warranty, unreliable or broken machines. But in a lot of cases, the whole
            freeze dryer was not bad. It still had value. So we started looking at freeze dryers the same way
            most people look at an older vehicle.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link to="/repair/quote" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-4 rounded-md transition-colors shadow-xl shadow-[#CE8946]/20" data-testid="about-hero-cta">
              <Wrench className="h-5 w-5" /> Start a free diagnostic
            </Link>
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-7 py-4 rounded-md hover:bg-white/10 transition-colors">
              <Phone className="h-5 w-5 text-[#CE8946]" /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Intro: the machine still has value */}
      <section className="py-16 sm:py-20 section-padding bg-white" data-testid="about-intro">
        <div className="max-w-4xl mx-auto">
          <p className="text-xl sm:text-2xl text-[#003456] font-semibold leading-relaxed">
            Their machines had control failures, electrical problems, refrigeration issues, or had simply
            reached the point where the owner was tired of rebuilding the same machine over and over.
          </p>
          <p className="text-slate-600 mt-5 leading-relaxed">
            But the chamber was still good. The cabinet was still good. The refrigeration system still had useful
            components. The vacuum system was still serviceable. The machine still had value. If you own a good
            truck and the transmission fails, you do not throw the truck away. If the dashboard quits, you do not
            scrap the engine. You look at the whole vehicle, figure out what is still good, find what is actually
            wrong, and rebuild or replace the systems that need attention, so it is dependable again.
          </p>
          <p className="text-[#003456] font-bold mt-5 leading-relaxed">
            That is exactly how we approach a freeze dryer.
          </p>
        </div>
      </section>

      {/* We don't just replace the part that failed */}
      <section className="py-16 sm:py-20 section-padding bg-[#003456]" data-testid="about-whole-system">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Whole machine thinking</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">We don't just replace the part that failed</h2>
            <p className="text-white/70 mt-5 leading-relaxed">
              Our parent company has spent years developing freeze drying technologies, controls and refrigeration
              systems. As more customers asked us to rebuild existing machines, we began testing that technology on
              select models, and quickly saw an opportunity to do more than swap a failed control board.
            </p>
            <p className="text-white/70 mt-4 leading-relaxed">
              A control board is only one part of the machine. Replacing the dashboard does not tell you whether the
              brakes, cooling system, wiring or drivetrain are healthy. When we rebuild, we look at the complete system.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {SYSTEMS.map((s) => (
              <div key={s.label} className="flex items-center gap-3 bg-[#023F63] border border-white/10 rounded-xl px-4 py-3" data-testid={`about-system-${s.label.split(' ')[0].toLowerCase()}`}>
                <span className="w-9 h-9 rounded-lg bg-[#CE8946]/15 flex items-center justify-center flex-shrink-0"><s.icon className="h-5 w-5 text-[#CE8946]" /></span>
                <span className="text-white text-sm font-medium leading-tight">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Like rebuilding an older work truck */}
      <SplitSection
        eyebrow="The right mindset"
        title="Think of it like rebuilding an older work truck"
        img={IMG.hero}
        imgAlt="Technician rebuilding a freeze dryer control housing"
        reverse
        bg="bg-[#F1F5F9]"
        testid="about-truck"
      >
        <p>
          A good work truck might have 200,000 kilometres on it and still have a perfectly good frame, body and
          engine. But maybe the suspension is worn, the gauges are failing, the electronics are outdated, and the
          wiring has been patched several times. At that point you have a choice: replace the whole truck, or
          rebuild the systems that are holding it back.
        </p>
        <p>
          That is the approach behind LyphLine. We keep the parts of the freeze dryer that still make sense to keep,
          then rebuild, replace or modernize the systems that no longer do, new controls for some, refrigeration
          work, electrical updates, heater management, protection systems or updated sensors for others.
        </p>
        <p className="text-[#003456] font-semibold">
          The machine does not have to be brand new to be useful. It just has to be rebuilt properly.
        </p>
      </SplitSection>

      {/* Refrigeration is part of the rebuild */}
      <SplitSection
        eyebrow="Refrigeration"
        title="Refrigeration is part of the rebuild"
        img={IMG.gauges}
        imgAlt="Servicing a freeze dryer refrigeration valve"
        bg="bg-white"
        testid="about-refrigeration"
      >
        <p>
          Just like an engine or transmission, a refrigeration system is designed around specific operating
          conditions. The compressor, refrigerant, lubricant, pressures, temperatures and components all have to
          work together.
        </p>
        <p>
          We have found machines where the refrigerant was not appropriate for the compressor or system, or was not
          appropriate for Canadian applications. An unsuitable refrigerant can mean poor cooling, higher compressor
          temperatures, lubrication problems, excessive pressures, lower efficiency and component damage over time.
        </p>
        <p className="text-[#003456] font-semibold">
          If a mechanic found the wrong transmission fluid in your truck, you would expect them to fix it, not
          ignore it because the truck still moved. We look at freeze dryers the same way.
        </p>
      </SplitSection>

      {/* Canadian requirements */}
      <section className="py-16 sm:py-20 section-padding bg-[#F1F5F9]" data-testid="about-canadian">
        <div className="max-w-4xl mx-auto bg-white border border-[#CE8946]/30 rounded-2xl p-8 sm:p-10">
          <span className="w-14 h-14 rounded-xl bg-[#CE8946]/15 flex items-center justify-center mb-5"><MapPin className="h-7 w-7 text-[#B87333]" /></span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#003456]">Canadian requirements matter too</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            A proper rebuild is not just about making something move, it still has to be safe and suitable. For
            machines rebuilt for use in Canada, we evaluate the systems that need to meet applicable electrical,
            refrigeration and safety requirements, wiring, electrical protection, refrigerant selection,
            refrigeration components, controls and other safety related systems. Where changes are required, we
            address them as part of the rebuild, and where a machine needs inspection, certification or field
            evaluation, that is considered too.
          </p>
          <p className="text-[#003456] font-semibold mt-4">
            Our goal is not to take a questionable machine, put a new screen on it, and call it fixed. We want the
            underlying system to make sense.
          </p>
          <TrustBadges variant="light" className="mt-6" />
        </div>
      </section>

      {/* Asset not appliance */}
      <SplitSection
        eyebrow="For businesses"
        title="A freeze dryer is an asset, not a disposable appliance"
        img={IMG.panel}
        imgAlt="Testing a freeze dryer control board"
        reverse
        bg="bg-white"
        testid="about-asset"
      >
        <p>
          A business owner looks at equipment differently. If your work truck constantly breaks down, the problem is
          not just the rebuild cost, it is lost time, missed jobs and lost productivity.
        </p>
        <p>
          The same happens with a freeze dryer. Downtime can mean delayed orders, lost production, wasted product,
          and hours spent troubleshooting instead of running a business. That is why we focus so heavily on
          reliability, serviceability and the entire system, turning an unreliable, broken or unsupported machine
          back into a useful piece of equipment.
        </p>
        <div className="flex items-center gap-3 bg-[#F1F5F9] border border-slate-200 rounded-xl px-5 py-4">
          <TrendingDown className="h-5 w-5 text-[#B87333] flex-shrink-0" />
          <span className="text-sm text-[#003456] font-medium">See what downtime is costing you with our calculator on the home page.</span>
        </div>
      </SplitSection>

      {/* Sometimes the best replacement is a rebuild */}
      <section className="py-16 sm:py-20 section-padding bg-[#003456]" data-testid="about-rebuild">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">Why we exist</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3">Sometimes the best replacement is a rebuild</h2>
          <p className="text-white/70 mt-5 leading-relaxed">
            LyphLine did not start because we wanted another brand. It started because customers asked us to help
            save machines they already owned. We tested our technology on selected models and found that, in many
            cases, there was too much good equipment left to justify throwing the whole machine away. Machines that
            were broken, unreliable or sitting unused were returned to service with updated technology, improved
            controls, rebuilt systems and a much more commercial operating philosophy.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mt-10 text-left">
            {[
              'Rebuild what is broken',
              'Replace what no longer makes sense',
              'Upgrade what makes it better',
            ].map((t) => (
              <div key={t} className="flex items-start gap-3 bg-[#023F63] border border-white/10 rounded-xl p-5">
                <CheckCircle2 className="h-5 w-5 text-[#CE8946] flex-shrink-0 mt-0.5" />
                <span className="text-white font-medium text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inside our shop */}
      <WorkshopGallery
        bg="bg-[#F1F5F9]"
        eyebrow="Inside the shop"
        title="This is where the rebuilds happen"
        subtitle="Every machine is diagnosed, rebuilt and tested by hand on our bench. These are real photos from our shop floor."
        testid="about-workshop-gallery"
      />

      {/* Closing CTA */}
      <section className="py-20 section-padding bg-white" data-testid="about-cta">
        <div className="max-w-5xl mx-auto bg-gradient-to-r from-[#003456] to-[#023F63] rounded-2xl px-6 sm:px-12 py-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Keep the machine. Fix what matters.</h2>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto leading-relaxed">
            If your freeze dryer still has a good foundation, it may have a lot more life left in it than you think.
            We look at the whole machine, not just the part that failed, rebuild it, modernize it, and put it back
            to work. Think of it as a full mechanical rebuild for your freeze dryer.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/repair/quote" className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-8 py-4 rounded-md transition-colors" data-testid="about-cta-quote">
              <Wrench className="h-5 w-5" /> Start my free diagnostic
            </Link>
            <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold px-8 py-4 rounded-md hover:bg-white/10 transition-colors">
              <Phone className="h-5 w-5 text-[#CE8946]" /> Call {CONTACT.phone}
            </a>
          </div>
          <div className="flex justify-center mt-8">
            <TrustBadges variant="dark" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
